import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';

const debug = {
  log: (action: string, ...args: any[]) => {
    console.log(`[Notes:${action}]`, new Date().toISOString(), ...args);
  },
  error: (action: string, error: any) => {
    console.error(`[Notes:${action}:ERROR]`, new Date().toISOString(), error);
  }
};

// Cache for storing notes
const notesCache = new Map<string, { content: string; timestamp: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

interface Note {
  id: string;
  content: string;
  created_at: string;
}

export function useNotes(devotionId: number) {
  const [notes, setNotes] = useState<Note[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  
  const mountedRef = useRef(true);
  const requestIdRef = useRef(0);
  const retryTimeoutRef = useRef<NodeJS.Timeout>();

  // Get current session on mount and when auth state changes
  useEffect(() => {
    const getSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUserId(session?.user?.id || null);
    };

    getSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id || null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const getCacheKey = useCallback((userId: string, devotionId: number) => {
    return `notes:${userId}:${devotionId}`;
  }, []);

  const clearCache = useCallback(() => {
    notesCache.clear();
  }, []);

  const fetchNotes = useCallback(async (retryCount = 0) => {
    if (!userId || !mountedRef.current) return;

    const requestId = ++requestIdRef.current;
    const cacheKey = getCacheKey(userId, devotionId);
    
    try {
      setIsLoading(true);
      setError(null);

      debug.log('fetchNotes:start', { devotionId, requestId, retryCount });

      const { data, error: fetchError } = await supabase
        .from('devotion_notes')
        .select('id, content, created_at')
        .eq('devotion_id', devotionId)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      // Only update state if this is still the current request
      if (mountedRef.current && requestId === requestIdRef.current) {
        debug.log('fetchNotes:success', { devotionId, requestId, count: data?.length });
        
        setNotes(data || []);
        setError(null);
      }
    } catch (error: any) {
      debug.error('fetchNotes', error);
      
      if (mountedRef.current && requestId === requestIdRef.current) {
        setError('Failed to load notes');

        // Retry with exponential backoff
        if (retryCount < 3) {
          const delay = Math.min(1000 * Math.pow(2, retryCount), 8000);
          retryTimeoutRef.current = setTimeout(() => {
            if (mountedRef.current) {
              fetchNotes(retryCount + 1);
            }
          }, delay);
        }
      }
    } finally {
      if (mountedRef.current && requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [devotionId, userId, getCacheKey]);

  const saveNote = async (content: string) => {
    if (!userId) {
      throw new Error('Must be signed in to save notes');
    }

    if (isSaving) return;

    const requestId = ++requestIdRef.current;

    try {
      setIsSaving(true);
      setError(null);

      debug.log('saveNote:start', { devotionId, requestId });

      const { data, error: saveError } = await supabase
        .from('devotion_notes')
        .insert([{
          devotion_id: devotionId,
          content,
          user_id: userId
        }])
        .select()
        .single();

      if (saveError) throw saveError;

      debug.log('saveNote:success', { devotionId, requestId });
      
      // Update local state with the new note
      if (mountedRef.current && requestId === requestIdRef.current) {
        setNotes(prev => [data, ...prev]);
      }
    } catch (error: any) {
      debug.error('saveNote', error);
      if (mountedRef.current && requestId === requestIdRef.current) {
        setError('Failed to save note');
      }
      throw error;
    } finally {
      if (mountedRef.current && requestId === requestIdRef.current) {
        setIsSaving(false);
      }
    }
  };

  const updateNote = async (noteId: string, content: string) => {
    if (!userId) {
      throw new Error('Must be signed in to update notes');
    }

    if (isSaving) return;

    const requestId = ++requestIdRef.current;

    try {
      setIsSaving(true);
      setError(null);

      debug.log('updateNote:start', { devotionId, noteId, requestId });

      // Optimistic update
      setNotes(prev => 
        prev.map(note => 
          note.id === noteId ? { ...note, content } : note
        )
      );

      const { error: updateError } = await supabase
        .from('devotion_notes')
        .update({ content })
        .eq('id', noteId)
        .eq('user_id', userId);

      if (updateError) throw updateError;

      debug.log('updateNote:success', { devotionId, noteId, requestId });
    } catch (error: any) {
      debug.error('updateNote', error);
      if (mountedRef.current && requestId === requestIdRef.current) {
        setError('Failed to update note');
        // Revert optimistic update
        fetchNotes();
      }
      throw error;
    } finally {
      if (mountedRef.current && requestId === requestIdRef.current) {
        setIsSaving(false);
      }
    }
  };

  const deleteNote = async (noteId: string) => {
    if (!userId) {
      throw new Error('Must be signed in to delete notes');
    }

    if (isSaving) return;

    const requestId = ++requestIdRef.current;

    try {
      setIsSaving(true);
      setError(null);

      debug.log('deleteNote:start', { devotionId, noteId, requestId });

      // Optimistic update
      setNotes(prev => prev.filter(note => note.id !== noteId));

      const { error: deleteError } = await supabase
        .from('devotion_notes')
        .delete()
        .eq('id', noteId)
        .eq('user_id', userId);

      if (deleteError) throw deleteError;

      debug.log('deleteNote:success', { devotionId, noteId, requestId });
    } catch (error: any) {
      debug.error('deleteNote', error);
      if (mountedRef.current && requestId === requestIdRef.current) {
        setError('Failed to delete note');
        // Revert optimistic update
        fetchNotes();
      }
      throw error;
    } finally {
      if (mountedRef.current && requestId === requestIdRef.current) {
        setIsSaving(false);
      }
    }
  };

  // Subscribe to realtime changes
  useEffect(() => {
    if (!userId) return;

    const subscription = supabase
      .channel('notes_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'devotion_notes',
          filter: `user_id=eq.${userId}`
        },
        () => {
          // Refetch on any changes
          fetchNotes();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [userId, fetchNotes]);

  // Initialize notes when devotionId or userId changes
  useEffect(() => {
    debug.log('effect:init', { devotionId });
    
    if (userId) {
      fetchNotes();
    } else {
      setNotes([]);
      setError(null);
      setIsLoading(false);
    }

    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [devotionId, userId, fetchNotes]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  return {
    notes,
    isLoading,
    error,
    isSaving,
    saveNote,
    updateNote,
    deleteNote,
    refreshNotes: fetchNotes,
    clearCache
  };
}